// src/api/subscriptions.ts
import axiosClient from './axiosClient';

/**
 * Тариф (вид абонемента) организации.
 *
 * Бэкенд отдаёт деньги в копейках — на экран переводим только при показе.
 */
export interface SubscriptionType {
    id: string;
    name: string;
    /** Цена тарифа в копейках. */
    price: number;
    currency: string;
    periodDays: number;
    isActive: boolean;
}

export interface SellSubscriptionPayload {
    subscriptionId: string;
    /** Сумма продажи в копейках. */
    totalPrice: number;
    remainingLessons: number;
    /** Продажа существующему клиенту справочника. */
    clientContactId?: string;
    /** Продажа «с нуля»: бэкенд сам заведёт клиента по имени и телефону. */
    clientName?: string;
    clientPhone?: string;
}

/** Проданный абонемент в том виде, в каком его вернул бэкенд. */
export interface MembershipSale {
    id: string;
    remainingLessons: number;
    soldAt: string;
    totalPrice: number;
    currency: string;
    totalLessons: number;
}

/** Списание занятия с проданного абонемента (история). */
export interface LessonUsageInfo {
    id: string;
    usedAt: string;
    revokedAt: string | null;
    revokeReason: string | null;
    isRevoked: boolean;
}

interface ApiResource {
    id: string;
    attributes: Record<string, unknown>;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
    typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;

/**
 * Разбор JSON:API-подобного ресурса `{ id, attributes }`.
 *
 * Часть эндпоинтов уже отдаёт плоские объекты, поэтому атрибуты
 * достаём терпимо: либо из `attributes`, либо с верхнего уровня.
 */
const asResource = (raw: unknown): ApiResource | null => {
    const record = asRecord(raw);
    if (!record) return null;

    const id = typeof record.id === 'string' ? record.id : null;
    if (!id) return null;

    const attributes = asRecord(record.attributes) ?? record;

    return { id, attributes };
};

const asString = (value: unknown, fallback: string): string =>
    typeof value === 'string' && value.trim() !== '' ? value : fallback;

const asInt = (value: unknown, fallback: number): number => {
    const parsed = typeof value === 'number' ? value : Number(value);

    return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
};

const asNullableString = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() !== '' ? value : null;

const parseSubscription = (raw: unknown): SubscriptionType | null => {
    const resource = asResource(raw);
    if (!resource) return null;

    return {
        id: resource.id,
        name: asString(resource.attributes.name, 'Абонемент'),
        price: asInt(resource.attributes.price, 0),
        currency: asString(resource.attributes.currency, 'RUB'),
        periodDays: asInt(resource.attributes.periodDays, 0),
        isActive: resource.attributes.isActive !== false,
    };
};

/**
 * Списание занятия. Бэкенд отдаёт ISO-даты; пустые и битые значения
 * приводим к `null`, чтобы экран не показывал «Invalid Date».
 */
const parseUsage = (raw: unknown): LessonUsageInfo | null => {
    const resource = asResource(raw);
    if (!resource) return null;

    return {
        id: resource.id,
        usedAt: asString(resource.attributes.usedAt, ''),
        revokedAt: asNullableString(resource.attributes.revokedAt),
        revokeReason: asNullableString(resource.attributes.revokeReason),
        isRevoked: resource.attributes.isRevoked === true,
    };
};

const parseSale = (raw: unknown): MembershipSale | null => {
    const resource = asResource(raw);
    if (!resource) return null;

    return {
        id: resource.id,
        remainingLessons: asInt(resource.attributes.remainingLessons, 0),
        soldAt: asString(resource.attributes.soldAt, ''),
        totalPrice: asInt(resource.attributes.totalPrice, 0),
        currency: asString(resource.attributes.currency, 'RUB'),
        totalLessons: asInt(resource.attributes.totalLessons, 0),
    };
};

/**
 * Тарифы организации. Без mode бэкенд отвечает 422 — организация
 * обязательна, прайс у каждой компании свой.
 */
export const fetchSubscriptionTypes = async (mode: string): Promise<SubscriptionType[]> => {
    const response = await axiosClient.get<{ data?: unknown }>('/v1/subscriptions', {
        params: { mode },
    });

    const raw = response.data?.data;

    return Array.isArray(raw)
        ? raw.map(parseSubscription).filter((item): item is SubscriptionType => item !== null)
        : [];
};

/**
 * Продажа абонемента клиенту.
 *
 * mode — в query, остальное в теле: так требует бэкенд.
 */
export const sellSubscription = async (
    mode: string,
    payload: SellSubscriptionPayload,
): Promise<MembershipSale | null> => {
    const response = await axiosClient.post<{ data?: unknown }>('/v1/membership-sales', payload, {
        params: { mode },
    });

    return parseSale(response.data?.data);
};

/** Списание одного занятия с абонемента. */
export const spendLesson = async (mode: string, saleId: string): Promise<MembershipSale | null> => {
    const response = await axiosClient.post<{ data?: unknown }>(
        `/v1/membership-sales/${saleId}/spend`,
        undefined,
        { params: { mode } },
    );

    return parseSale(response.data?.data);
};

/**
 * История списаний занятий по проданному абонементу.
 *
 * Бэкенд отдаёт списания по убыванию usedAt, порядок не пересортировываем:
 * свежие сверху — так и читается в карточке.
 */
export const fetchSaleUsages = async (mode: string, saleId: string): Promise<LessonUsageInfo[]> => {
    const response = await axiosClient.get<{ data?: unknown }>(
        `/v1/membership-sales/${saleId}/usages`,
        { params: { mode } },
    );

    const raw = response.data?.data;

    return Array.isArray(raw)
        ? raw.map(parseUsage).filter((item): item is LessonUsageInfo => item !== null)
        : [];
};
